import { useEffect, useState } from "react";
import {
  Layers,
  Loader2,
  Plus,
  Pencil,
  Trash2,
  Palette,
  Users,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { colorStyles, fmtHours } from "@/pages/schedule/helpers";
import ShiftTemplateFormDialog from "@/pages/schedule/ShiftTemplateFormDialog";

// ---------------------------------------------------------------------------
// TemplatesManagerDialog
// Egy nagy overlay, amelyen az aktív telephely összes shift sablonja
// listázódik. Lehet újat hozzáadni, szerkeszteni és törölni — minden
// CRUD a beágyazott ShiftTemplateFormDialog-ot használja, hogy egy helyen
// éljen a forma kódja.
// ---------------------------------------------------------------------------
export default function TemplatesManagerDialog({
  open,
  onOpenChange,
  siteId,
  siteName,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const reload = async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/shift-templates?site_id=${siteId}`);
      setTemplates(data || []);
    } catch (e) {
      toast.error(formatApiError(e) || "Sablonok betöltése sikertelen.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && siteId) reload();
  }, [open, siteId]);

  const onDelete = async (t) => {
    if (!window.confirm(`Törlöd a(z) „${t.name}" sablont?`)) return;
    setDeletingId(t._id);
    try {
      await api.delete(`/shift-templates/${t._id}`);
      toast.success("Sablon törölve.");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e) || "Törlés sikertelen.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-xl"
          data-testid="templates-manager-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-4 w-4" strokeWidth={1.75} />
              Sablonok kezelése
            </DialogTitle>
            <DialogDescription>
              {siteName ? (
                <>
                  Telephely: <b>{siteName}</b> · a sablonok kizárólag itt
                  érhetők el.
                </>
              ) : (
                "Válassz egy telephelyet először."
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="-mx-2 mt-1 max-h-[55vh] overflow-y-auto px-2">
            {loading ? (
              <div className="grid h-32 place-items-center">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : templates.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-6 py-10 text-center dark:border-border dark:bg-foreground/[0.02]">
                <div className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-400 dark:border-border dark:bg-card">
                  <Palette className="h-4 w-4" strokeWidth={1.75} />
                </div>
                <p className="text-sm font-medium">Még nincs sablon</p>
                <p className="max-w-xs text-[12px] text-slate-500 dark:text-muted-foreground">
                  Hozd létre az első műszak sablont — pl. „Délelőtt 06–14&rdquo;.
                </p>
              </div>
            ) : (
              <ul className="space-y-2">
                {templates.map((t) => (
                  <TemplateRow
                    key={t._id}
                    template={t}
                    onEdit={() => {
                      setEditing(t);
                      setFormOpen(true);
                    }}
                    onDelete={() => onDelete(t)}
                    deleting={deletingId === t._id}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 dark:border-border">
            <p className="text-[11px] text-slate-500 dark:text-muted-foreground">
              {templates.length === 0
                ? "Hozd létre az első sablont alább."
                : `${templates.length} sablon a telephelyhez`}
            </p>
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              data-testid="add-template-from-manager"
            >
              <Plus className="h-3.5 w-3.5" />
              Új sablon
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Inner CRUD form */}
      <ShiftTemplateFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        siteId={siteId}
        siteName={siteName}
        onSaved={reload}
      />
    </>
  );
}

function TemplateRow({ template, onEdit, onDelete, deleting }) {
  const cs = colorStyles(template.color);
  return (
    <li
      className="group/row relative flex items-center gap-3 overflow-hidden rounded-xl border border-slate-200 bg-white pl-3 pr-2 py-2.5 shadow-[0_1px_2px_-1px_rgba(15,23,42,0.05)] transition-all hover:border-slate-300 hover:shadow-[0_6px_16px_-10px_rgba(15,23,42,0.18)] dark:border-border dark:bg-card"
      data-testid={`tpl-row-${template._id}`}
    >
      <span
        className={cn("h-10 w-1 shrink-0 rounded-full", cs.dot)}
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-[13px] font-semibold tracking-tight">
            {template.name}
          </p>
          <span className="font-mono text-[10px] font-semibold tabular-nums text-slate-500 dark:text-muted-foreground">
            {fmtHours(template.duration_hours)}h
          </span>
        </div>
        <p className="mt-0.5 font-mono text-[11px] tabular-nums text-slate-500 dark:text-muted-foreground">
          {template.start_time}–{template.end_time}
          {template.unpaid_break_minutes > 0 && (
            <span className="text-slate-400"> · -{template.unpaid_break_minutes}m szünet</span>
          )}
        </p>
        {template.notes && (
          <p className="mt-0.5 truncate text-[10.5px] text-slate-400">
            {template.notes}
          </p>
        )}
      </div>
      <div className="flex items-center gap-1">
        <TemplateAccessPopover template={template} />
        <button
          type="button"
          onClick={onEdit}
          className="grid h-8 w-8 place-items-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-muted-foreground dark:hover:bg-foreground/[0.06] dark:hover:text-foreground"
          aria-label="Szerkesztés"
          data-testid={`tpl-edit-${template._id}`}
        >
          <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className="grid h-8 w-8 place-items-center rounded-md text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
          aria-label="Törlés"
          data-testid={`tpl-delete-${template._id}`}
        >
          {deleting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
          )}
        </button>
      </div>
    </li>
  );
}

function TemplateAccessPopover({ template }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/shift-templates/${template._id}/allowed-employees`,
        );
        if (!cancelled) setItems(data?.items || []);
      } catch (e) {
        toast.error(formatApiError(e) || "Lekérés sikertelen.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, template._id]);

  const toggle = (id) => {
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, allowed: !it.allowed } : it)));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/shift-templates/${template._id}/allowed-employees`, {
        employee_ids: items.filter((it) => it.allowed).map((it) => it.id),
      });
      toast.success("Engedélyek mentve.");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  const allowedCount = items.filter((it) => it.allowed).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="grid h-8 w-8 place-items-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-muted-foreground dark:hover:bg-foreground/[0.06] dark:hover:text-foreground"
          aria-label="Engedélyezett dolgozók"
          data-testid={`tpl-access-${template._id}`}
        >
          <Users className="h-3.5 w-3.5" strokeWidth={1.75} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        className="w-72 p-0"
        data-testid={`tpl-access-popover-${template._id}`}
      >
        <div className="border-b border-border px-3 py-2.5">
          <p className="text-[12.5px] font-semibold tracking-tight">
            Kik használhatják?
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            A „Tervezet&rdquo; leadásánál ezek a dolgozók választhatják.
          </p>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {loading ? (
            <div className="grid place-items-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : items.length === 0 ? (
            <p className="px-3 py-4 text-center text-[11.5px] text-muted-foreground">
              Nincs dolgozó ennél a telephelynél.
            </p>
          ) : (
            <ul>
              {items.map((it) => (
                <li key={it.id}>
                  <button
                    type="button"
                    onClick={() => toggle(it.id)}
                    data-testid={`tpl-access-toggle-${it.id}`}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-muted/60"
                  >
                    <span
                      className={cn(
                        "grid h-4 w-4 shrink-0 place-items-center rounded border transition-colors",
                        it.allowed
                          ? "border-blue-500 bg-blue-500 text-white"
                          : "border-border bg-card",
                      )}
                    >
                      {it.allowed && <Check className="h-3 w-3" strokeWidth={3} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <p className="truncate text-[12.5px] font-medium">{it.name}</p>
                      {it.position && (
                        <p className="truncate text-[10.5px] text-muted-foreground">{it.position}</p>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-border px-3 py-2">
          <p className="text-[10.5px] text-muted-foreground">
            {allowedCount} engedélyezve
          </p>
          <Button
            size="sm"
            className="h-7 text-[11.5px]"
            onClick={onSave}
            disabled={saving || loading}
            data-testid={`tpl-access-save-${template._id}`}
          >
            {saving ? (
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
            ) : (
              <Check className="mr-1 h-3 w-3" strokeWidth={2.5} />
            )}
            Mentés
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
